import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Container, IconButton,
    Divider, TextField, Button, Collapse,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    EmojiEventsOutlined, TrendingUpOutlined,
    LockOutlined, EditOutlined,
    NorthEastOutlined, StarOutlined,
    AutoGraphOutlined, LightbulbOutlined,
    PaymentsOutlined, CreditScoreOutlined,
    CalendarTodayOutlined, ArrowBackOutlined,
} from '@mui/icons-material';

/* ─── Brand ──────────────────────────────────────────────────────── */
const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    accent:  '#C8B8E8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    red:     '#FF4D6A',
    gold:    '#F5C842',
    amber:   '#FF9500',
};

const CURRENT_SCORE = 653;
const MAX_SCORE     = 850;

const milestones = [
    { icon: <PaymentsOutlined />,     title: 'Pay all bills on time',          sub: '3 month streak',          timeline: '3 months',        color: B.green,  pts: '+40 pts' },
    { icon: <CreditScoreOutlined />,  title: 'Reduce credit utilisation',      sub: 'Keep below 30%',          timeline: '1–2 billing cycles', color: B.amber,  pts: '+35 pts' },
    { icon: <CalendarTodayOutlined />, title: 'Avoid new credit applications', sub: 'No hard enquiries',       timeline: '6 months',        color: B.light,  pts: '+15 pts' },
    { icon: <AutoGraphOutlined />,    title: 'Grow Credit Builder savings',    sub: 'Reach ₦600k target',      timeline: '4 months',        color: B.purple, pts: '+25 pts' },
];

const tips = [
    { emoji: '📊', text: 'Review your Spending Analysis to find easy savings.' },
    { emoji: '🔔', text: 'Set payment reminders for every bill to avoid late fees.' },
    { emoji: '🏦', text: 'Link all bank accounts for a more accurate score.' },
    { emoji: '💰', text: 'Automate monthly Credit Builder contributions.' },
];

function useCountUp(target: number, duration = 900) {
    const [val, setVal] = useState(0);
    const raf = useRef<number>(0);
    useEffect(() => {
        let start: number | null = null;
        const step = (ts: number) => {
            if (!start) start = ts;
            const p = Math.min((ts - start) / duration, 1);
            const ease = 1 - Math.pow(1 - p, 3);
            setVal(Math.round(ease * target));
            if (p < 1) raf.current = requestAnimationFrame(step);
        };
        raf.current = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf.current);
    }, [target, duration]);
    return val;
}

const GoalSettingPage: React.FC = () => {
    const navigate = useNavigate();
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';

    const [rawInput,  setRawInput]  = useState('');
    const [savedGoal, setSavedGoal] = useState<number | null>(null);
    const [editing,   setEditing]   = useState(false);
    const [showTips,  setShowTips]  = useState(false);

    const targetScore = parseInt(rawInput) || 0;
    const isValid     = targetScore > CURRENT_SCORE && targetScore <= MAX_SCORE;

    const handleSave = () => {
        if (!isValid) return;
        setSavedGoal(targetScore);
        setEditing(false);
    };

    const progress    = savedGoal ? Math.min(100, (CURRENT_SCORE / savedGoal) * 100) : 0;
    const ptsNeeded   = savedGoal ? savedGoal - CURRENT_SCORE : 0;
    const displayGoal = useCountUp(savedGoal || 0, 900);
    const displayPts  = useCountUp(ptsNeeded, 700);

    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;

    return (
        <Box sx={{ minHeight: '100vh', background: isDark ? B.deep : B.lavender, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO ═══════════════════════════════════════════════ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-56px', sm: 0 },
                pt: { xs: 'calc(56px + 44px)', sm: '44px' },
                pb: 14, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.gold, .15)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -20, left: -40, width: 180, height: 180, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .12)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3.5}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <IconButton onClick={() => navigate(-1)} sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                            <ArrowBackOutlined sx={{ fontSize: 17 }} />
                        </IconButton>
                        <Box>
                            <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>Credit Score</Typography>
                            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.15rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>Goal Setting</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .6, background: alpha(B.gold, .15), border: `1px solid ${alpha(B.gold, .3)}`, borderRadius: 8, px: 1.25, py: .5 }}>
                        <EmojiEventsOutlined sx={{ fontSize: 13, color: B.gold }} />
                        <Typography sx={{ color: B.gold, fontSize: '.62rem', fontWeight: 700, letterSpacing: '.06em' }}>GOAL MODE</Typography>
                    </Box>
                </Box>

                <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ color: alpha('#fff', .35), fontSize: '.63rem', letterSpacing: '.18em', textTransform: 'uppercase', mb: 1 }}>Current Score</Typography>
                    <Box display="flex" alignItems="baseline" justifyContent="center" gap={1.5} mb={.75}>
                        <Typography sx={{ color: '#fff', fontFamily: "'Instrument Serif', serif", fontSize: { xs: '4.5rem', sm: '5.5rem' }, fontWeight: 400, lineHeight: 1, letterSpacing: '-.03em' }}>
                            {CURRENT_SCORE}
                        </Typography>
                        <Box>
                            <Typography sx={{ color: B.green, fontWeight: 700, fontSize: '.88rem' }}>▲ Good</Typography>
                            <Typography sx={{ color: alpha('#fff', .3), fontSize: '.62rem' }}>/ {MAX_SCORE}</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ maxWidth: 300, mx: 'auto' }}>
                        <Box sx={{ height: 5, borderRadius: 6, background: alpha('#fff', .1), overflow: 'hidden' }}>
                            <Box sx={{ width: `${(CURRENT_SCORE / MAX_SCORE) * 100}%`, height: '100%', background: `linear-gradient(90deg, ${B.purple}, ${B.green})`, borderRadius: 6 }} />
                        </Box>
                        <Box display="flex" justifyContent="space-between" mt={.6}>
                            <Typography sx={{ color: alpha('#fff', .25), fontSize: '.6rem' }}>300</Typography>
                            <Typography sx={{ color: alpha('#fff', .25), fontSize: '.6rem' }}>850</Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: -8, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── Goal entry / editing ── */}
                {(!savedGoal || editing) && (
                    <Box sx={{ mb: 2, borderRadius: 3, overflow: 'hidden', background: `linear-gradient(145deg, ${B.dark} 0%, ${B.mid} 100%)`, boxShadow: `0 16px 48px ${alpha(B.deep, .45)}` }}>
                        <Box sx={{ p: 2.75 }}>
                            <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
                                <Box sx={{ width: 38, height: 38, borderRadius: 2.5, background: alpha(B.gold, .15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <StarOutlined sx={{ color: B.gold, fontSize: 19 }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '.9rem', letterSpacing: '-.02em' }}>{editing ? 'Update your goal' : 'Set a target score'}</Typography>
                                    <Typography sx={{ color: alpha('#fff', .38), fontSize: '.7rem' }}>Must be above {CURRENT_SCORE}</Typography>
                                </Box>
                            </Box>

                            {/* Big score preview */}
                            <Box sx={{ background: alpha('#fff', .06), border: `1px solid ${alpha('#fff', rawInput ? .18 : .1)}`, borderRadius: 2.5, px: 2, py: 1.5, mb: .5, transition: 'border-color .2s' }}>
                                <Box display="flex" alignItems="baseline" gap={1.5}>
                                    <Typography sx={{ fontFamily: "'Instrument Serif', serif", fontSize: '3rem', color: isValid ? B.gold : alpha('#fff', .22), lineHeight: 1, letterSpacing: '-.02em', transition: 'color .2s', minWidth: 120 }}>
                                        {rawInput || '—'}
                                    </Typography>
                                    <Box>
                                        <Typography sx={{ color: alpha('#fff', .25), fontSize: '.65rem', letterSpacing: '.06em' }}>/ {MAX_SCORE}</Typography>
                                        {isValid && (
                                            <Typography sx={{ color: B.green, fontSize: '.65rem', fontWeight: 700 }}>+{targetScore - CURRENT_SCORE} pts needed</Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                            <Box sx={{ mb: 2, height: '1.5px', background: `linear-gradient(90deg, ${alpha(B.gold, .35)}, transparent)` }} />

                            {/* Quick chips */}
                            <Typography sx={{ color: alpha('#fff', .25), fontSize: '.62rem', letterSpacing: '.14em', textTransform: 'uppercase', mb: 1 }}>Quick targets</Typography>
                            <Box display="flex" gap={1} flexWrap="wrap" mb={2.5}>
                                {[700, 720, 750, 780, 800, 850].filter(s => s > CURRENT_SCORE).map(s => (
                                    <Box key={s} onClick={() => setRawInput(String(s))} sx={{
                                        px: 1.5, py: .55, borderRadius: 10, cursor: 'pointer',
                                        background: rawInput === String(s) ? alpha(B.gold, .2) : alpha('#fff', .07),
                                        border: `1px solid ${rawInput === String(s) ? alpha(B.gold, .4) : alpha('#fff', .1)}`,
                                        transition: 'all .18s',
                                        '&:hover': { background: alpha(B.gold, .12), borderColor: alpha(B.gold, .25) },
                                    }}>
                                        <Typography sx={{ color: rawInput === String(s) ? B.gold : alpha('#fff', .55), fontSize: '.75rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{s}</Typography>
                                    </Box>
                                ))}
                            </Box>

                            <TextField fullWidth size="small" type="number" placeholder="Or type any score…"
                                       value={rawInput} onChange={e => setRawInput(e.target.value)}
                                       inputProps={{ min: CURRENT_SCORE + 1, max: MAX_SCORE }}
                                       sx={{
                                           mb: 2,
                                           '& .MuiOutlinedInput-root': {
                                               borderRadius: 2.5, fontSize: '.84rem', color: '#fff',
                                               background: alpha('#fff', .06),
                                               '& fieldset': { borderColor: alpha('#fff', .12) },
                                               '&:hover fieldset': { borderColor: alpha('#fff', .25) },
                                               '&.Mui-focused fieldset': { borderColor: alpha(B.gold, .5) },
                                           },
                                           '& input': { color: '#fff' },
                                           '& input::placeholder': { color: alpha('#fff', .25) },
                                       }}
                            />

                            <Button fullWidth onClick={handleSave} disabled={!isValid} sx={{
                                background: isValid ? `linear-gradient(135deg, ${B.gold}, ${B.amber})` : alpha('#fff', .08),
                                color: isValid ? B.dark : alpha('#fff', .25),
                                borderRadius: 2.5, textTransform: 'none', fontWeight: 800, fontSize: '.88rem', py: 1.4,
                                boxShadow: isValid ? `0 8px 24px ${alpha(B.gold, .35)}` : 'none',
                                transition: 'all .25s',
                                '&.Mui-disabled': { background: alpha('#fff', .07), color: alpha('#fff', .2) },
                            }}>
                                {editing ? 'Update Goal' : 'Set Goal'}
                            </Button>
                            {editing && (
                                <Button fullWidth onClick={() => setEditing(false)} sx={{ mt: 1, color: alpha('#fff', .35), textTransform: 'none', fontSize: '.78rem' }}>
                                    Cancel
                                </Button>
                            )}
                        </Box>
                    </Box>
                )}

                {/* ── Active goal display ── */}
                {savedGoal && !editing && (
                    <Box sx={{ mb: 2, borderRadius: 3, overflow: 'hidden', background: `linear-gradient(145deg, ${B.dark} 0%, ${B.mid} 100%)`, boxShadow: `0 16px 48px ${alpha(B.deep, .45)}` }}>
                        <Box sx={{ p: 2.75 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2.5}>
                                <Box>
                                    <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.16em', textTransform: 'uppercase', mb: .5 }}>Your Goal</Typography>
                                    <Box display="flex" alignItems="baseline" gap={1.25}>
                                        <Typography sx={{ color: B.gold, fontFamily: "'Instrument Serif', serif", fontSize: '3.2rem', fontWeight: 400, lineHeight: 1, letterSpacing: '-.02em' }}>
                                            {displayGoal}
                                        </Typography>
                                        <Box>
                                            <Typography sx={{ color: alpha('#fff', .3), fontSize: '.65rem' }}>/ {MAX_SCORE}</Typography>
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .4, background: alpha(B.gold, .15), border: `1px solid ${alpha(B.gold, .25)}`, borderRadius: 6, px: .8, py: .25, mt: .25 }}>
                                                <NorthEastOutlined sx={{ fontSize: 10, color: B.gold }} />
                                                <Typography sx={{ color: B.gold, fontSize: '.6rem', fontWeight: 700 }}>+{displayPts} pts</Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                                <IconButton onClick={() => { setRawInput(String(savedGoal)); setEditing(true); }} sx={{ color: alpha('#fff', .4), background: alpha('#fff', .07), border: `1px solid ${alpha('#fff', .1)}`, width: 34, height: 34, '&:hover': { color: '#fff' } }}>
                                    <EditOutlined sx={{ fontSize: 15 }} />
                                </IconButton>
                            </Box>

                            <Box display="flex" justifyContent="space-between" mb={.75}>
                                <Typography sx={{ color: alpha('#fff', .35), fontSize: '.65rem' }}>Current: <span style={{ color: '#fff', fontWeight: 700 }}>{CURRENT_SCORE}</span></Typography>
                                <Typography sx={{ color: alpha('#fff', .35), fontSize: '.65rem' }}>Target: <span style={{ color: B.gold, fontWeight: 700 }}>{savedGoal}</span></Typography>
                            </Box>
                            <Box sx={{ height: 8, borderRadius: 6, background: alpha('#fff', .08), overflow: 'hidden', mb: .6 }}>
                                <Box sx={{
                                    width: `${progress}%`, height: '100%', borderRadius: 6,
                                    background: `linear-gradient(90deg, ${B.purple}, ${B.gold})`,
                                    boxShadow: `0 0 12px ${alpha(B.gold, .4)}`,
                                    transition: 'width 1.2s cubic-bezier(.16,1,.3,1)',
                                }} />
                            </Box>
                            <Box display="flex" justifyContent="flex-end">
                                <Typography sx={{ color: B.gold, fontSize: '.65rem', fontWeight: 700 }}>{progress.toFixed(1)}% there</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ px: 2.75, py: 1.5, background: alpha('#000', .2), display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUpOutlined sx={{ fontSize: 13, color: B.green }} />
                            <Typography sx={{ color: alpha('#fff', .4), fontSize: '.68rem' }}>
                                At your current rate, you'll hit <span style={{ color: B.green, fontWeight: 700 }}>{savedGoal}</span> in ~4 months
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* ── Milestones ── */}
                <Box sx={{ mb: 2, borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, overflow: 'hidden' }}>
                    <Box sx={{ px: 2.5, pt: 2.25, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}` }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: 2, background: alpha(B.gold, .1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <EmojiEventsOutlined sx={{ color: B.gold, fontSize: 16 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '.88rem', letterSpacing: '-.02em', color: isDark ? '#fff' : B.ink }}>Milestones</Typography>
                            <Typography sx={{ fontSize: '.66rem', color: alpha(isDark ? '#fff' : B.ink, .35) }}>Steps to reach your goal</Typography>
                        </Box>
                    </Box>
                    {milestones.map((m, i) => (
                        <React.Fragment key={i}>
                            <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'flex-start', gap: 1.75 }}>
                                <Box sx={{ width: 36, height: 36, borderRadius: 2.5, background: alpha(m.color, .1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    {React.cloneElement(m.icon as React.ReactElement, { sx: { color: m.color, fontSize: 17 } })}
                                </Box>
                                <Box flex={1}>
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                        <Box>
                                            <Typography sx={{ fontWeight: 600, fontSize: '.84rem', color: isDark ? '#fff' : B.ink, letterSpacing: '-.01em', mb: .15 }}>{m.title}</Typography>
                                            <Typography sx={{ fontSize: '.7rem', color: alpha(isDark ? '#fff' : B.ink, .38) }}>{m.sub} · {m.timeline}</Typography>
                                        </Box>
                                        <Box sx={{ background: alpha(m.color, .12), border: `1px solid ${alpha(m.color, .2)}`, borderRadius: 8, px: .9, py: .3, flexShrink: 0, ml: 1 }}>
                                            <Typography sx={{ color: m.color, fontSize: '.65rem', fontWeight: 700 }}>{m.pts}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                            {i < milestones.length - 1 && <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .055) }} />}
                        </React.Fragment>
                    ))}
                </Box>

                {/* ── Tips ── */}
                <Box sx={{ mb: 3, borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, overflow: 'hidden' }}>
                    <Box onClick={() => setShowTips(v => !v)} sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', '&:hover': { background: alpha(isDark ? '#fff' : B.dark, .025) } }}>
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <Box sx={{ width: 32, height: 32, borderRadius: 2, background: alpha(B.purple, .1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <LightbulbOutlined sx={{ color: B.purple, fontSize: 16 }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: 700, fontSize: '.88rem', letterSpacing: '-.02em', color: isDark ? '#fff' : B.ink }}>Personalised Tips</Typography>
                                <Typography sx={{ fontSize: '.66rem', color: alpha(isDark ? '#fff' : B.ink, .35) }}>Tap to {showTips ? 'hide' : 'reveal'}</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ transition: 'transform .25s', transform: showTips ? 'rotate(180deg)' : 'none' }}>
                            <AutoGraphOutlined sx={{ fontSize: 16, color: alpha(isDark ? '#fff' : B.ink, .25) }} />
                        </Box>
                    </Box>
                    <Collapse in={showTips}>
                        <Box sx={{ borderTop: `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}` }}>
                            {tips.map((t, i) => (
                                <React.Fragment key={i}>
                                    <Box sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                        <Box sx={{ width: 32, height: 32, borderRadius: 2, background: alpha(isDark ? '#fff' : B.dark, .04), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.9rem', flexShrink: 0 }}>
                                            {t.emoji}
                                        </Box>
                                        <Typography sx={{ fontSize: '.8rem', color: alpha(isDark ? '#fff' : B.ink, .55), lineHeight: 1.6, pt: .4 }}>{t.text}</Typography>
                                    </Box>
                                    {i < tips.length - 1 && <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .055) }} />}
                                </React.Fragment>
                            ))}
                        </Box>
                    </Collapse>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                        256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default GoalSettingPage;